import React, { useMemo, useState, useRef, useContext, useCallback, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
    FormTitle,
    LoginContainer,
    LoginContent,
    RegisterContainer,
    RegisterIntro,
    Container,
    FormText,
} from "./styled";

import Button from "components/Form/Button";
import ContainerUnauthenticated from "containers/Unauthenticated";
import { exposeStrapiError } from "utils";
import { ButtonContainer } from "ui/styled";
import FormSuccess from "components/FormSuccess";
import { CoreContext } from "context/CoreContext";
import useI18n from "hooks/useI18n";
import Core from "components/Form/Core";
import TermCheck from "components/TermCheck";
import { DoMVUEssentialsRegister, ValidateMVUEssentialsInvite } from "services/authentication";
import { getPortalRegistrationSuccess, isParticipantLimitError } from "utils/portalRegistration";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function MVUEssentialsRegister() {
    const history = useHistory();
    const { setModal } = useContext(CoreContext);
    const { t } = useI18n();
    const { code } = useParams();
    const formRef = useRef();
    const recaptchaRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [register, setRegister] = useState({});
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [success, setSuccess] = useState(null);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [inviteValidated, setInviteValidated] = useState(false);

    const navigate = useCallback(
        (to) => history.push(`/${to}`),
        [history]
    );

    const onCaptchaChange = useCallback((token) => {
        setRecaptchaToken(token || null);
    }, []);

    const resetCaptcha = useCallback(() => {
        recaptchaRef?.current?.reset?.();
        setRecaptchaToken(null);
    }, []);

    const exposeRegistrationError = useCallback((result) => {
        if (isParticipantLimitError(result)) {
            toast.error(t("portal_registration_participant_limit_reached"));
            return true;
        }
        return exposeStrapiError(result);
    }, [t]);

    const completeLogin = useCallback(() => {
        navigate("mvu-lite/login");
    }, [navigate]);

    const formItems = useMemo(
        () => [
            {
                ref: "full_name",
                placeholder: t("full_name"),
                required: true,
                full: true,
            },
            {
                custom: <FormText>{t("registration_professional_email_hint")}</FormText>,
                full: true,
            },
            {
                ref: "email",
                id: "email",
                placeholder: t("email"),
                required: true,
                half: true,
                type: "email",
            },
            {
                ref: "company_name",
                placeholder: t("company_name"),
                required: true,
                half: true,
                disabled: Boolean(String(register.company_name || '').trim()),
            },
            {
                ref: "invitation_code",
                placeholder: t("invitation_code"),
                required: true,
                full: true,
                value: code,
                disabled: true,
            },
            {
                ref: "password",
                placeholder: t("password"),
                required: true,
                half: true,
                type: "password",
            },
            {
                ref: "cpassword",
                placeholder: t("password_confirmation"),
                required: true,
                half: true,
                type: "password",
            },
        ],
        [t, code, register.company_name]
    );

    const buildPayload = useCallback(() => {
        const form = formRef?.current?.getForm?.(false);
        if (!form) return null;

        if (!termsAccepted) {
            toast.error(t("accept_terms") || "Please accept the terms.");
            return null;
        }

        if (!recaptchaToken) {
            toast.error(
                t("please_confirm_you_are_not_a_robot")
            );
            return null;
        }

        const payload = {
            ...form,
        };
        if (!code) {
            toast.error("Invalid or missing invitation code.");
            return null;
        }

        if (form.password !== form.cpassword) {
            toast.error(t("password_and_password_confirmation_must_match"));
            return null;
        }

        if (!form.password?.match(/[A-Z]/)) {
            toast.error(t('one_uppercase_letter'));
            return null;
        }
        if (!form.password?.match(/[0-9]/)) {
            toast.error(t('one_number'));
            return null;
        }
        if ((form.password || '').length < 6) {
            toast.error(t('at_least_6_characters'));
            return null;
        }
        if (!form.password?.match(/[!@#$%^&*()\-\+=\{\}\[\]\\|;:,.\/?'\"~<>`´]/)) {
            toast.error(t('at_least_1_special_character'));
            return null;
        }

        if (String(form.invitation_code || '').trim() !== String(code || '').trim()) {
            toast.error("Invitation code does not match the link.");
            return null;
        }

        return payload;
    }, [termsAccepted, recaptchaToken, t, code]);

    const submit = useCallback(async () => {
        if (!inviteValidated) {
            toast.error("This invite link is not valid.");
            return;
        }

        const payload = buildPayload();
        if (!payload) return;

        setLoading(true);
        const result = await DoMVUEssentialsRegister({
            full_name: payload.full_name,
            email: String(payload.email || '').trim().toLowerCase(),
            institution: payload.company_name,
            invite_code: code,
            password: payload.password,
            passwordConfirmation: payload.cpassword,
        });

        if (result && !exposeRegistrationError(result)) {
            const confirmation = getPortalRegistrationSuccess("lite", result);
            setSuccess({
                title: t(confirmation.titleKey),
                text: t(confirmation.textKey),
                label: t(confirmation.actionKey),
                action: completeLogin,
            });
            resetCaptcha();
        } else {
            resetCaptcha();
        }

        setLoading(false);
    }, [buildPayload, t, completeLogin, resetCaptcha, code, inviteValidated, exposeRegistrationError]);

    const back = useCallback(() => {
        navigate("mvu-lite/login");
    }, [navigate]);

    useEffect(() => {
        let cancelled = false;

        const validateInvite = async () => {
            if (!code) {
                if (!cancelled) {
                    toast.error("Invalid or missing invitation code.");
                    navigate("mvu-lite/login");
                }
                return;
            }

            const result = await ValidateMVUEssentialsInvite(code);
            if (cancelled) return;

            const hasError = exposeRegistrationError(result);
            if (!hasError && result?.valid) {
                setInviteValidated(true);
                setRegister((prev) => ({
                    ...prev,
                    invitation_code: code,
                    company_name: result.company_name || '',
                }));
                return;
            }

            setInviteValidated(false);
            if (!hasError) toast.error(t("portal_registration_invite_invalid"));
            navigate("mvu-lite/login");
        };

        validateInvite();
        return () => { cancelled = true; };
    }, [code, exposeRegistrationError, navigate, t]);

    return (
        <>
            {!success ? null : <FormSuccess success={success} actionLabel={success?.label} />}

            {success ? null : (
                <ContainerUnauthenticated students>
                    <LoginContent>
                        <LoginContainer>
                            <RegisterIntro>
                                <FormTitle>
                                    {t("mvu_essentials_register_welcome")}
                                </FormTitle>

                                <FormText>
                                    {t("mvu_essentials_register_access_notice")}
                                </FormText>
                            </RegisterIntro>

                            <RegisterContainer>
                                <Core ref={formRef} formItems={formItems} register={register} />

                                <Container>
                                    <TermCheck
                                        title={t("i_accept_the")}
                                        text={t("privacy_policy")}
                                        value={termsAccepted}
                                        onChange={setTermsAccepted}
                                        link={() => setModal({ type: "term" })}
                                    />
                                </Container>

                                <div style={{ margin: "12px 0" }}>
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={RECAPTCHA_SITE_KEY}
                                        onChange={onCaptchaChange}
                                    />
                                </div>

                                <ButtonContainer space>
                                    <Button color="blue" outline onClick={back}>{t("back") || "Back"}</Button>
                                    <Button color="blue" loading={loading} onClick={submit}>{t("send") || "Send"}</Button>
                                </ButtonContainer>
                            </RegisterContainer>
                        </LoginContainer>
                    </LoginContent>
                </ContainerUnauthenticated>
            )}
        </>
    );
}
